import { useEffect, useMemo, useRef, useState } from 'react';
import { ParentChildrenOnboarding } from '@/components/parent/parent-children-onboarding';
import { PlayerOnboardingStep } from '@/components/player/player-onboarding-step';
import {
  isAccountTypeChange,
  translateAccountTypeChangeError,
  useAccountTypeChangeAllowed,
} from '@/lib/account-type-change';
import { consumeReturnTo, hasPendingReturnTo } from '@/lib/return-to';
import { isFirstScratchUse } from '@/features/scratch-tutorial/first-use';
import { useNavigate } from '@tanstack/react-router';
import type { SupabaseClient } from '@supabase/supabase-js';
import { Check } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  CompassLogo,
  Label,
  cn,
} from '@jeraisonne/ui';
import {
  FORMATION_TYPES,
  PARENTAL_CONSENT_AGE_THRESHOLD,
  SECONDARY_SCHOOL_LEVELS,
  SUBJECTS,
  type FormationType,
  type SchoolLevel,
  type SubjectSlug,
  type UserRole,
} from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { computeAgeYears } from '@/lib/age';
import {
  CPGE_SCHOOL_LEVELS,
  canSubmitHigherEd,
  higherEdProfileFields,
  isCpgeFormation,
} from '@/lib/cpge-profile';
import { requestParentalConsent } from '@/lib/parental-consent';
import { ConsentWaitingScreen } from '@/components/consent-waiting-screen';
import {
  OnboardingDiagnosticChoice,
  onboardingFinishLabel,
} from '@/features/skill-diagnostic/onboarding-diagnostic-step';
import { markDiagnosticPrompted, useDiagnosticState } from '@/lib/skill-diagnostic';
import { readDecouverteProfile } from '@/features/decouverte/decouverte-profile';
import {
  fetchTutorInvitePublic,
  redeemTutorInviteLink,
  translateInviteError,
  type TutorInvitePublicContext,
} from '@/lib/tutor-invite';
import {
  guessDisplayName,
  onboardingStepLabels,
  roleStepSubmitLabel,
  subjectsAfterLevelChange,
  type RoleCardId,
} from '@/lib/onboarding-flow';
import {
  ChoiceChip,
  ONBOARDING_FOOTER_SPACE,
  OnboardingBack,
  OnboardingFooter,
  OnboardingSteps,
} from '@/features/onboarding/onboarding-ui';

type Step = 'role' | 'student' | 'objectives' | 'parent' | 'player' | 'waiting-consent';

/**
 * Cartes du choix de rôle. "Élève" et "Étudiant" partagent toutes deux
 * `role: 'student'` en base (student_profiles.is_higher_ed_student les
 * distingue) mais sont deux cartes SÉPARÉES dans l'UI — l'étudiant du
 * supérieur choisit sa carte dès cette étape, plus de toggle "Tu es…" plus
 * bas (la question alternance ne se pose ensuite QUE pour cette carte).
 */
const ROLE_CARDS: ReadonlyArray<{
  id: RoleCardId;
  role: Exclude<UserRole, 'admin'>;
  higherEd: boolean;
  emoji: string;
  label: string;
  description: string;
}> = [
  {
    id: 'student',
    role: 'student',
    higherEd: false,
    emoji: '🎒',
    label: 'Élève',
    description: 'Je suis au collège ou au lycée',
  },
  {
    id: 'student_higher_ed',
    role: 'student',
    higherEd: true,
    emoji: '🎓',
    label: 'Étudiant',
    description: 'Études supérieures (licence, BTS, prépa…)',
  },
  {
    id: 'parent',
    role: 'parent',
    higherEd: false,
    emoji: '👨‍👩‍👧',
    label: 'Parent',
    description: 'Je veux suivre la progression de mon enfant',
  },
  {
    id: 'tutor',
    role: 'tutor',
    higherEd: false,
    emoji: '🧑‍🏫',
    label: 'Professeur',
    description: 'Je donne des cours particuliers',
  },
  {
    id: 'player',
    role: 'player',
    higherEd: false,
    emoji: '🎮',
    label: 'Curieux',
    description: 'Je refais des maths pour moi',
  },
];

// Carte du rôle d'un profil déjà écrit (reprise d'un onboarding entamé).
function cardOfRole(role: string | null | undefined): RoleCardId | null {
  if (role === 'student' || role === 'parent' || role === 'tutor' || role === 'player') return role;
  return null;
}

// Marges internes des cartes d'étape : plus serrées sur mobile (390 px).
const CARD_HEADER = 'px-5 sm:px-6';
const CARD_CONTENT = 'px-5 sm:px-6';

// ─── Composant principal ───────────────────────────────────────────────────────

export function OnboardingPage() {
  const { user, profile, loading, profileLoading, refreshProfile } = useAuth();
  const navigate = useNavigate();

  // Compte qui change de type depuis « Mon profil » (?changer=1) : on repart
  // de l'étape « rôle », sans la carte du type actuel.
  // Recalculé à chaque rendu : au rechargement de la page le profil arrive
  // APRÈS le 1er rendu ; figé trop tôt, le mode restait éteint et la page
  // renvoyait le compte vers son tableau de bord.
  const changingType = isAccountTypeChange(window.location.search, profile?.role);
  // Mineur (élève < 15 ans, compte enfant géré) : pas de changement de type.
  const accountTypeChange = useAccountTypeChangeAllowed(
    changingType ? user?.id : null,
    profile?.role,
  );
  // Le type actuel n'est pas proposé (sauf élève : il peut passer en étudiant
  // ou refaire son profil élève).
  const roleCards = changingType
    ? ROLE_CARDS.filter((c) => c.role === 'student' || c.role !== profile?.role)
    : ROLE_CARDS;

  useEffect(() => {
    if (changingType && accountTypeChange.allowed === false) navigate({ to: '/profile' });
  }, [changingType, accountTypeChange.allowed, navigate]);

  // Initialise l'étape depuis le profil existant (onboarding entamé, ex. parent).
  const [step, setStep] = useState<Step>(() => {
    if (!profile || changingType) return 'role';
    if (profile.role === 'student' || profile.role === 'parent' || profile.role === 'player')
      return profile.role;
    return 'role';
  });

  // Profil choisi sur l'accueil `/decouverte` (élève, parent, prof) : la carte
  // de rôle est déjà cochée, on ne repose pas la question. Jamais pour un
  // changement de type ni un lien d'invitation (qui fixe le rôle lui-même).
  const [decouverteProfile] = useState(() =>
    !profile && !changingType && !new URLSearchParams(window.location.search).get('invite')
      ? readDecouverteProfile()
      : null,
  );

  // Champs de l'étape "role"
  const [selectedRole, setSelectedRole] = useState<Exclude<UserRole, 'admin'> | null>(
    profile && !changingType
      ? (profile.role as Exclude<UserRole, 'admin'>)
      : (decouverteProfile ?? null),
  );
  // Carte visuellement sélectionnée (distingue Élève / Étudiant, tous deux role='student').
  const [selectedRoleCard, setSelectedRoleCard] = useState<RoleCardId | null>(() =>
    profile && !changingType ? cardOfRole(profile.role) : (decouverteProfile ?? null),
  );
  const [displayName, setDisplayName] = useState(
    () =>
      profile?.display_name ??
      guessDisplayName({
        fullName: user?.user_metadata?.full_name as string | undefined,
        name: user?.user_metadata?.name as string | undefined,
        email: user?.email,
      }),
  );

  // Champs de l'étape "student"
  const [birthdate, setBirthdate] = useState('');
  // Niveau pré-défini quand on arrive depuis un bouton « Live School » du
  // marketing (route /live/<niveau> → /onboarding?niveau=<niveau>).
  const [level, setLevel] = useState<SchoolLevel | ''>(() => {
    const n = new URLSearchParams(window.location.search).get('niveau');
    return SECONDARY_SCHOOL_LEVELS.some((l) => l.value === n) ? (n as SchoolLevel) : '';
  });
  const [subjects, setSubjects] = useState<SubjectSlug[]>(() =>
    level ? subjectsAfterLevelChange([], level) : [],
  );
  const [parentEmail, setParentEmail] = useState('');
  const [consentEmail, setConsentEmail] = useState('');
  // Retour depuis l'écran d'attente pour corriger l'email du parent : la date
  // de naissance (qui a déclenché l'accord parental) n'est plus modifiable.
  const [consentLocked, setConsentLocked] = useState(false);

  // Profil "Étudiant" post-bac — orthogonal à level/subjects (cf. CLAUDE.md,
  // school_level reste 100% secondaire). level/subjects restent vides dans ce cas.
  const [isHigherEd, setIsHigherEd] = useState(false);
  const [formationType, setFormationType] = useState<FormationType | ''>('');
  const [studyYear, setStudyYear] = useState<number | ''>('');
  // Filière de prépa (formation « cpge », migration 0302) = student_profiles.level.
  const [cpgeLevel, setCpgeLevel] = useState<SchoolLevel | ''>('');
  const [isAlternant, setIsAlternant] = useState(false);
  // classe de l'exercice guidé de la prise en main du brouillon (fin de l'inscription)
  const tutorialNiveau = cpgeLevel || level || undefined;

  // Lien d'invitation tuteur (migration 0241) — `?invite=<token>` posé par la
  // page publique /rejoindre/$token après signup/login. Force le rôle "élève"
  // (matières préremplies) ou "étudiant alternance" (isAlternant verrouillé),
  // masque le sélecteur de rôle, et lie le compte au tuteur en fin d'étape.
  const [inviteToken] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get('invite'),
  );
  const [inviteContext, setInviteContext] = useState<TutorInvitePublicContext | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const inviteActive = !!inviteToken && !inviteError;

  // Étape "objectives" : depuis le 31/10/2026 elle ne propose plus d'objectifs, seulement le diagnostic (le nom
  // de l'étape est gardé)
  const [loading2, setLoading2] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [readyToNavigate, setReadyToNavigate] = useState(false);
  // Diagnostic de programme proposé en tête de l'étape objectifs (0320) : il
  // n'existe qu'une fois `student_profiles` écrit, d'où l'activation à cette étape.
  const diagnosticState = useDiagnosticState(user?.id, step === 'objectives');
  const diagnosticAvailable = !!diagnosticState.data?.available && !!diagnosticState.data.current;
  const [diagnosticNow, setDiagnosticNow] = useState(true);
  const startDiagnostic = diagnosticAvailable && diagnosticNow;

  // Prénom du profil existant (changement de type) arrivé après le 1er rendu :
  // il remplace la proposition tant que l'utilisateur n'a rien tapé.
  const nameTouched = useRef(false);
  useEffect(() => {
    if (profile?.display_name && !nameTouched.current) setDisplayName(profile.display_name);
  }, [profile?.display_name]);

  // Prénom proposé une fois l'utilisateur chargé (l'initialiseur de useState
  // passe avant la résolution de l'auth).
  useEffect(() => {
    if (!user || displayName) return;
    const name = guessDisplayName({
      fullName: user.user_metadata?.full_name as string | undefined,
      name: user.user_metadata?.name as string | undefined,
      email: user.email,
    });
    if (name) setDisplayName(name);
  }, [user]);

  // Fetch du contexte public du lien d'invitation (sans session requise — la
  // RPC est anon-callable). Pré-sélectionne la carte de rôle correspondante.
  useEffect(() => {
    if (!inviteToken) return;
    let cancelled = false;
    setInviteLoading(true);
    fetchTutorInvitePublic(inviteToken)
      .then((ctx) => {
        if (cancelled) return;
        setInviteContext(ctx);
        setSelectedRole('student');
        if (ctx.kind === 'academic') {
          setSelectedRoleCard('student_higher_ed');
          setIsHigherEd(true);
          setIsAlternant(true);
        } else {
          setSelectedRoleCard('student');
          setIsHigherEd(false);
          setSubjects(ctx.subjects);
        }
      })
      .catch((err) => {
        if (!cancelled)
          setInviteError(translateInviteError(err instanceof Error ? err.message : ''));
      })
      .finally(() => {
        if (!cancelled) setInviteLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [inviteToken]);

  const age = useMemo(() => computeAgeYears(birthdate), [birthdate]);
  const requiresConsent = age !== null && age < PARENTAL_CONSENT_AGE_THRESHOLD;

  // ── Redirection initiale pour les utilisateurs déjà onboardés ───────────────
  const checkedRef = useRef(false);
  useEffect(() => {
    if (loading || profileLoading || checkedRef.current) return;
    checkedRef.current = true;

    if (!profile) return; // nouvel utilisateur → reste sur /onboarding
    if (changingType) return; // changement de type : on reste sur l'étape « rôle »

    // Compte existant arrivé ici depuis une page publique (carte SKILLCARD) :
    // on l'y ramène plutôt que vers son tableau de bord.
    const back = consumeReturnTo();
    if (back) {
      window.location.assign(back);
      return;
    }

    const role = profile.role as Exclude<UserRole, 'admin'>;

    if (role === 'tutor') {
      navigate({ to: '/tutor/dashboard' });
      return;
    }
    if (role === 'parent') {
      navigate({ to: '/parent' });
      return;
    }
    if (role === 'player') {
      navigate({ to: '/player/dashboard' });
      return;
    }

    if (role === 'student') {
      supabase
        .from('student_profiles')
        .select('id')
        .eq('id', user!.id)
        .maybeSingle()
        .then(({ data }) => {
          if (data) navigate({ to: '/student/dashboard' });
          // sinon : affiche l'étape 'student'
        });
    }
  }, [loading, profileLoading, profile, user, navigate, changingType]);

  // Navigue vers le bon dashboard une fois le profil chargé.
  useEffect(() => {
    if (!readyToNavigate || loading || profileLoading || !profile) return;
    // Nouveau compte créé depuis une page publique (carte scannée) : on y revient.
    const back = consumeReturnTo();
    if (back) {
      window.location.assign(back);
      return;
    }
    // Inscription d'un élève ou d'un curieux : la prise en main du brouillon (exercice guidé) avant le tableau de
    // bord, sauf s'il l'a déjà faite sur cet appareil (démo sans compte comprise). « Passer » reste possible.
    if (
      !changingType &&
      (profile.role === 'student' || profile.role === 'player') &&
      isFirstScratchUse()
    ) {
      navigate({
        to: '/brouillon/tutoriel',
        search: { niveau: tutorialNiveau, apres: 'tableau' },
      });
      return;
    }
    if (profile.role === 'student') {
      navigate({ to: '/student/dashboard' });
    } else if (profile.role === 'tutor' || profile.role === 'admin') {
      navigate({ to: '/tutor/dashboard' });
    } else if (profile.role === 'player') {
      navigate({ to: '/player/dashboard' });
    } else {
      navigate({ to: '/parent' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- niveau et changement de type lus au moment de partir
  }, [readyToNavigate, loading, profileLoading, profile, navigate]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  function handleSelectRoleCard(cardId: RoleCardId) {
    const card = ROLE_CARDS.find((c) => c.id === cardId);
    if (!card) return;
    setSelectedRoleCard(cardId);
    setSelectedRole(card.role);
    setIsHigherEd(card.higherEd);
    setError(null);
  }

  function pickLevel(next: SchoolLevel) {
    setLevel(next);
    setSubjects((cur) => subjectsAfterLevelChange(cur, next));
  }

  function toggleSubject(slug: SubjectSlug) {
    setSubjects((prev) => (prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]));
  }

  function goBack(to: Step) {
    setError(null);
    setStep(to);
    window.scrollTo({ top: 0 });
  }

  function messageOf(err: unknown): string {
    return err instanceof Error ? err.message : ((err as { message?: string })?.message ?? '');
  }

  /**
   * Écrit `profiles` (rôle + prénom). Élève, étudiant et curieux ne l'écrivent
   * qu'à la VALIDATION de leur dernière étape (rien d'écrit tant qu'ils n'ont
   * pas fini : pas de compte « à moitié » qui atterrirait sur un tableau de
   * bord sans classe) ; parent et prof dès l'étape « rôle » (le parent en a
   * besoin pour créer le compte de son enfant).
   * Changement de type : on ne touche qu'au rôle et au nom (l'avatar et la
   * langue du compte sont conservés).
   */
  async function saveProfile() {
    if (!user || !selectedRole) throw new Error('Session expirée, reconnecte-toi.');
    const { error: err } = changingType
      ? await supabase
          .from('profiles')
          .update({
            role: selectedRole as UserRole,
            display_name: displayName.trim().slice(0, 80),
          })
          .eq('id', user.id)
      : await supabase.from('profiles').upsert({
          id: user.id,
          role: selectedRole as UserRole,
          display_name: displayName.trim().slice(0, 80),
          locale: 'fr',
          avatar_url: profile?.avatar_url ?? null,
        });
    if (err) throw err;
  }

  async function handleSetRole(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedRole || !displayName.trim()) return;
    if (!user) {
      setError('Session expirée, reconnecte-toi.');
      return;
    }
    setError(null);
    if (selectedRole === 'student' || selectedRole === 'player') {
      setStep(selectedRole);
      window.scrollTo({ top: 0 });
      return;
    }
    setLoading2(true);
    try {
      await saveProfile();
      if (selectedRole === 'tutor') {
        // Rien d'autre à saisir : le tableau de bord du prof le guide
        // (carte « Premiers pas » : élèves, créneaux, classe).
        await refreshProfile();
        setReadyToNavigate(true);
        return;
      }
      setStep(selectedRole);
      window.scrollTo({ top: 0 });
    } catch (err) {
      setError(translateAccountTypeChangeError(messageOf(err)));
    } finally {
      setLoading2(false);
    }
  }

  const canSubmitStudent = isHigherEd
    ? Boolean(birthdate) && canSubmitHigherEd({ formationType, cpgeLevel, studyYear })
    : Boolean(birthdate && level && subjects.length > 0);
  const consentEmailMissing = requiresConsent && !parentEmail.trim();

  // Ce qui manque encore, affiché au-dessus du bouton (désactivé) de l'étape.
  const studentMissing = !birthdate
    ? 'ta date de naissance'
    : consentEmailMissing
      ? 'l’email d’un parent'
      : isHigherEd
        ? !formationType
          ? 'ta formation'
          : isCpgeFormation(formationType)
            ? !cpgeLevel
              ? 'ta prépa'
              : null
            : !studyYear
              ? 'ton année d’étude'
              : null
        : !level
          ? 'ta classe'
          : subjects.length === 0
            ? 'au moins une matière'
            : null;

  async function handleStudentProfile(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmitStudent) return;
    if (!user) {
      setError('Session expirée, reconnecte-toi.');
      return;
    }
    if (consentEmailMissing) {
      setError("Tu as moins de 15 ans : l'email d'un parent est obligatoire (RGPD).");
      return;
    }
    setError(null);
    setLoading2(true);
    try {
      await saveProfile();
      const higherEd = isHigherEd
        ? higherEdProfileFields({ formationType, cpgeLevel, studyYear })
        : null;
      // `subjects_reviewed_at`/`is_higher_ed_student`/`formation_type`/`study_year`/
      // `is_alternant` (migrations 0226/0237) sont postérieurs aux types générés
      // (stale, cf. CLAUDE.md) → client générique pour cet upsert.
      const { error: err } = await (supabase as unknown as SupabaseClient)
        .from('student_profiles')
        .upsert({
          id: user!.id,
          birthdate,
          level: higherEd ? higherEd.level : level,
          subjects: higherEd ? higherEd.subjects : subjects,
          is_higher_ed_student: isHigherEd,
          formation_type: higherEd ? higherEd.formation_type : null,
          study_year: higherEd ? higherEd.study_year : null,
          is_alternant: isHigherEd ? isAlternant : false,
          subjects_reviewed_at: new Date().toISOString(),
          parental_consent_required: requiresConsent,
          parental_consent_status: requiresConsent ? 'pending' : 'active',
          parental_consent_email: requiresConsent ? parentEmail.trim() : null,
          parental_consent_at: requiresConsent ? null : new Date().toISOString(),
        });
      if (err) throw err;

      if (inviteToken && inviteContext) {
        try {
          await redeemTutorInviteLink(inviteToken);
        } catch (redeemErr) {
          // Non bloquant : le compte est créé, on ne casse pas l'onboarding
          // pour un souci de liaison (lien révoqué entre-temps, etc.) — le
          // tuteur pourra relier l'élève manuellement depuis /tutor/contacts.
          console.error('[onboarding] redemption du lien tuteur échouée', redeemErr);
        }
      }

      if (requiresConsent) {
        setConsentEmail(parentEmail.trim());
        // Envoie le mail d'accord au parent (lien magique). Non bloquant :
        // l'écran d'attente permet de renvoyer en cas d'échec réseau.
        try {
          await requestParentalConsent(parentEmail.trim());
        } catch (sendErr) {
          console.error('[onboarding] envoi accord parental', sendErr);
        }
        setStep('waiting-consent');
      } else if (isHigherEd) {
        // Pas d'objectifs pour un profil étudiant post-bac (keyés school_level).
        await refreshProfile();
        setReadyToNavigate(true);
      } else {
        // Diagnostic + objectifs avant le tableau de bord.
        setStep('objectives');
        window.scrollTo({ top: 0 });
      }
    } catch (err) {
      setError(translateAccountTypeChangeError(messageOf(err)) || 'Erreur inattendue.');
    } finally {
      setLoading2(false);
    }
  }

  async function handleObjectivesStep() {
    if (!user) return;
    setError(null);
    setLoading2(true);
    try {
      await refreshProfile();
      // Retour attendu vers une carte scannée : il prime sur le diagnostic.
      if (startDiagnostic && !hasPendingReturnTo()) {
        // le diagnostic se joue sur la feuille : d'abord sa prise en main (une fois)
        if (isFirstScratchUse())
          void navigate({
            to: '/brouillon/tutoriel',
            search: { niveau: tutorialNiveau, apres: 'diagnostic' },
          });
        else void navigate({ to: '/student/diagnostic', search: { start: 1 } });
        return;
      }
      // « Plus tard » : la fenêtre du tableau de bord ne le reproposera pas aussitôt.
      const diagnosticId = diagnosticState.data?.current?.id;
      if (diagnosticAvailable && !diagnosticNow && diagnosticId) {
        void markDiagnosticPrompted(diagnosticId);
      }
      setReadyToNavigate(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inattendue.');
    } finally {
      setLoading2(false);
    }
  }

  async function handleFinish() {
    setLoading2(true);
    setError(null);
    try {
      // Curieux : le profil n'est écrit qu'ici (cf. saveProfile).
      if (step === 'player') await saveProfile();
      await refreshProfile();
      setReadyToNavigate(true);
    } catch (err) {
      setError(translateAccountTypeChangeError(messageOf(err)));
    } finally {
      setLoading2(false);
    }
  }

  // ── Rendu ────────────────────────────────────────────────────────────────────

  if (step === 'waiting-consent') {
    return (
      <ConsentWaitingScreen
        email={consentEmail}
        onRefresh={() => window.location.reload()}
        onEditEmail={() => {
          setConsentLocked(true);
          goBack('student');
        }}
      />
    );
  }

  const stepLabels = onboardingStepLabels(selectedRoleCard);
  const stepIndex = step === 'role' ? 0 : step === 'objectives' ? 2 : 1;
  // Le lien d'invitation fixe le rôle : pas de retour vers la grille.
  const backToRole = inviteActive || consentLocked ? undefined : () => goBack('role');

  const errorAlert = error && (
    <Alert variant="error">
      <AlertDescription>{error}</AlertDescription>
    </Alert>
  );

  const inviteBanner = inviteActive && inviteContext && (
    <Alert variant="info">
      <AlertDescription>
        Invité·e par <strong>{inviteContext.tutorDisplayName}</strong>
        {inviteContext.kind === 'academic'
          ? ' : tu seras relié·e à ton tuteur académique, en alternance.'
          : ' : tu seras relié·e à ton prof dès ton profil validé.'}
      </AlertDescription>
    </Alert>
  );

  return (
    <div className="min-h-dvh bg-transparent">
      <header className="px-4 pb-2 pt-5 sm:px-6 sm:py-6">
        <CompassLogo size="md" wordmarkClassName="text-ink-900" />
      </header>

      <main className="mx-auto flex w-full max-w-2xl flex-col px-4 pb-10 pt-2 sm:px-6 sm:pt-8">
        <OnboardingSteps labels={stepLabels} current={stepIndex} />

        {/* ── Étape 1 : choix du rôle ─────────────────────────────────────── */}
        {step === 'role' && (
          <form onSubmit={handleSetRole} className={ONBOARDING_FOOTER_SPACE}>
            <Card className="border-ink-100/60 shadow-lg">
              <CardHeader className={CARD_HEADER}>
                <CardTitle>
                  {changingType
                    ? 'Changer de type de compte'
                    : inviteActive
                      ? 'Bienvenue sur JeRaisonne 🎉'
                      : 'Qui es-tu ?'}
                </CardTitle>
                <CardDescription>
                  {changingType
                    ? 'Choisis ton nouveau type de compte. Tes exercices déjà faits et tes SKILLCARDS sont conservés.'
                    : inviteActive
                      ? 'Plus que ton prénom, puis ton profil, et c’est parti.'
                      : 'On adapte JeRaisonne à ce que tu viens y faire.'}
                </CardDescription>
              </CardHeader>
              <CardContent className={cn(CARD_CONTENT, 'space-y-5')}>
                {inviteToken && inviteError && (
                  <Alert variant="warning">
                    <AlertDescription>
                      <strong>Ce lien d’invitation ne fonctionne pas.</strong> {inviteError} Tu peux
                      quand même créer ton compte : ton prof pourra te relier ensuite.
                    </AlertDescription>
                  </Alert>
                )}

                {inviteActive ? (
                  inviteLoading || !inviteContext ? (
                    <div className="flex justify-center py-6">
                      <div className="border-primary-400 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
                    </div>
                  ) : (
                    inviteBanner
                  )
                ) : (
                  <div
                    role="radiogroup"
                    aria-label="Type de compte"
                    className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-3"
                  >
                    {roleCards.map((card, i) => {
                      const selected = selectedRoleCard === card.id;
                      const lastAlone = i === roleCards.length - 1 && roleCards.length % 2 === 1;
                      return (
                        <button
                          key={card.id}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          onClick={() => handleSelectRoleCard(card.id)}
                          className={cn(
                            'flex min-h-[3.75rem] w-full items-center gap-3 rounded-2xl border-2 px-3 py-2.5 text-left transition-colors active:scale-[0.98]',
                            lastAlone && 'sm:col-span-2',
                            selected
                              ? 'border-primary-500 bg-primary-50 ocean:bg-primary-500/15'
                              : 'border-ink-100 hover:border-primary-300 ocean:bg-white/5 bg-white',
                          )}
                        >
                          <span className="w-9 shrink-0 text-center text-2xl" aria-hidden="true">
                            {card.emoji}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span
                              className={cn(
                                'block text-base font-semibold',
                                selected ? 'text-primary-700' : 'text-ink-900',
                              )}
                            >
                              {card.label}
                            </span>
                            <span className="text-ink-500 block text-sm leading-snug">
                              {card.description}
                            </span>
                          </span>
                          <span
                            aria-hidden
                            className={cn(
                              'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2',
                              selected
                                ? 'border-primary-500 bg-primary-500 text-white'
                                : 'border-ink-200',
                            )}
                          >
                            {selected && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label htmlFor="display_name" required>
                    Comment doit-on t'appeler ?
                  </Label>
                  <Input
                    id="display_name"
                    required
                    autoComplete="given-name"
                    placeholder="Ton prénom"
                    maxLength={80}
                    value={displayName}
                    onChange={(e) => {
                      nameTouched.current = true;
                      setDisplayName(e.target.value);
                    }}
                  />
                  <p className="text-ink-500 text-xs">
                    {selectedRole === 'student'
                      ? 'Seuls tes profs et tes parents le verront : les autres élèves voient un pseudo (modifiable dans ton profil).'
                      : 'Visible sur ton profil.'}
                  </p>
                </div>
              </CardContent>
            </Card>

            <OnboardingFooter
              hint={
                !error && !selectedRole && !inviteActive
                  ? 'Choisis ton type de compte pour continuer.'
                  : undefined
              }
            >
              {errorAlert}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={loading2}
                disabled={
                  !selectedRole ||
                  !displayName.trim() ||
                  loading2 ||
                  (inviteActive && !inviteContext) ||
                  (changingType && accountTypeChange.allowed !== true)
                }
              >
                {roleStepSubmitLabel(selectedRoleCard)}
              </Button>
              {changingType && (
                <Button
                  type="button"
                  variant="ghost"
                  fullWidth
                  onClick={() => navigate({ to: '/profile' })}
                >
                  Annuler
                </Button>
              )}
            </OnboardingFooter>
          </form>
        )}

        {/* ── Étape 2a : profil élève / étudiant ──────────────────────────── */}
        {step === 'student' && (
          <form onSubmit={handleStudentProfile} className={ONBOARDING_FOOTER_SPACE}>
            {backToRole && <OnboardingBack onClick={backToRole} />}
            <Card className="border-ink-100/60 shadow-lg">
              <CardHeader className={CARD_HEADER}>
                <CardTitle>{isHigherEd ? 'Ta formation 🎓' : 'Ta classe 🎒'}</CardTitle>
                <CardDescription>
                  {isHigherEd
                    ? 'Pour te proposer le bon programme.'
                    : 'Pour te proposer les exercices de ton programme.'}
                </CardDescription>
              </CardHeader>
              <CardContent className={cn(CARD_CONTENT, 'space-y-6')}>
                {inviteBanner}

                {/* Date de naissance (+ email parent juste dessous si < 15 ans) */}
                <div>
                  <Label htmlFor="birthdate" required>
                    Ta date de naissance
                  </Label>
                  <Input
                    id="birthdate"
                    type="date"
                    required
                    max={new Date().toISOString().slice(0, 10)}
                    value={birthdate}
                    readOnly={consentLocked}
                    aria-readonly={consentLocked}
                    onChange={(e) => {
                      if (!consentLocked) setBirthdate(e.target.value);
                    }}
                  />
                  {age !== null && (
                    <p className="text-ink-500 mt-1 text-sm">
                      Tu as {age} an{age > 1 ? 's' : ''}.
                      {consentLocked && ' Corrige l’email de ton parent ci-dessous.'}
                    </p>
                  )}
                </div>

                {requiresConsent && (
                  <div className="border-accent-200 bg-accent-50/60 ocean:border-white/10 ocean:bg-white/5 space-y-2 rounded-2xl border p-4">
                    <Label htmlFor="parent_email" required>
                      Email d'un parent
                    </Label>
                    <p className="text-ink-600 text-sm">
                      Avant 15 ans, la loi (RGPD) demande l'accord d'un parent. On lui envoie un
                      email : tu commences dès qu'il a validé.
                    </p>
                    <Input
                      id="parent_email"
                      type="email"
                      required
                      inputMode="email"
                      placeholder="parent@exemple.com"
                      autoComplete="email"
                      value={parentEmail}
                      onChange={(e) => setParentEmail(e.target.value)}
                    />
                  </div>
                )}

                {!isHigherEd ? (
                  <>
                    {/* Classe */}
                    <fieldset>
                      <legend className="text-ink-700 text-sm font-medium">
                        Ta classe à la rentrée <span className="text-error-500 ml-1">*</span>
                      </legend>
                      <div className="mt-1.5 grid grid-cols-4 gap-2">
                        {SECONDARY_SCHOOL_LEVELS.map((l) => (
                          <ChoiceChip
                            key={l.value}
                            selected={level === l.value}
                            onClick={() => pickLevel(l.value)}
                            className={l.value === 'tle' ? 'col-span-2' : undefined}
                          >
                            {l.label}
                          </ChoiceChip>
                        ))}
                      </div>
                    </fieldset>

                    {/* Matières : celles de la classe choisie, maths pré-cochées */}
                    {level && (
                      <fieldset>
                        <legend className="text-ink-700 text-sm font-medium">
                          Tes matières <span className="text-error-500 ml-1">*</span>
                        </legend>
                        <div className="mt-1.5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                          {SUBJECTS.filter((s) => s.levels.includes(level)).map((s) => {
                            const isSelected = subjects.includes(s.value);
                            return (
                              <label
                                key={s.value}
                                className={cn(
                                  'flex min-h-[3rem] cursor-pointer items-center gap-3 rounded-xl border-2 px-3 py-2 transition-colors active:scale-[0.98]',
                                  isSelected
                                    ? 'border-primary-500 bg-primary-50 ocean:bg-primary-500/15'
                                    : 'border-ink-100 hover:border-primary-300 ocean:bg-white/5 bg-white',
                                )}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleSubject(s.value)}
                                  className="sr-only"
                                />
                                <span
                                  className="w-7 shrink-0 text-center text-xl"
                                  aria-hidden="true"
                                >
                                  {s.emoji}
                                </span>
                                <span
                                  className={cn(
                                    'min-w-0 flex-1 text-sm font-semibold',
                                    isSelected ? 'text-primary-700' : 'text-ink-900',
                                  )}
                                >
                                  {s.label}
                                </span>
                                <span
                                  aria-hidden
                                  className={cn(
                                    'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2',
                                    isSelected
                                      ? 'border-primary-500 bg-primary-500 text-white'
                                      : 'border-ink-200',
                                  )}
                                >
                                  {isSelected && <Check className="h-3 w-3" strokeWidth={3} />}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </fieldset>
                    )}
                  </>
                ) : (
                  <>
                    {/* Formation */}
                    <fieldset>
                      <legend className="text-ink-700 text-sm font-medium">
                        Ta formation <span className="text-error-500 ml-1">*</span>
                      </legend>
                      <div className="mt-1.5 grid grid-cols-2 gap-2 sm:grid-cols-3">
                        {FORMATION_TYPES.map((f) => (
                          <ChoiceChip
                            key={f.value}
                            selected={formationType === f.value}
                            onClick={() => setFormationType(f.value)}
                          >
                            {f.label}
                          </ChoiceChip>
                        ))}
                      </div>
                    </fieldset>

                    {isCpgeFormation(formationType) ? (
                      /* Filière de prépa : donne accès au programme et aux SKILLCARDS */
                      <fieldset>
                        <legend className="text-ink-700 text-sm font-medium">
                          Ta prépa <span className="text-error-500 ml-1">*</span>
                        </legend>
                        <div className="mt-1.5 grid grid-cols-2 gap-2 sm:grid-cols-4">
                          {CPGE_SCHOOL_LEVELS.map((l) => (
                            <ChoiceChip
                              key={l.value}
                              selected={cpgeLevel === l.value}
                              onClick={() => setCpgeLevel(l.value)}
                            >
                              {l.label}
                              <span className="text-ink-500 block text-xs font-normal">
                                {l.value === 'mpsi' || l.value === 'ptsi'
                                  ? '1re année'
                                  : '2e année'}
                              </span>
                            </ChoiceChip>
                          ))}
                        </div>
                      </fieldset>
                    ) : (
                      formationType && (
                        // Année d'étude
                        <div>
                          <Label htmlFor="study_year" required>
                            Année d'étude
                          </Label>
                          <Input
                            id="study_year"
                            type="number"
                            inputMode="numeric"
                            min={1}
                            max={8}
                            required
                            placeholder="Ex : 2 pour une 2e année"
                            value={studyYear}
                            onChange={(e) =>
                              setStudyYear(e.target.value ? Number(e.target.value) : '')
                            }
                          />
                        </div>
                      )
                    )}

                    {/* Alternance — masquée si déjà verrouillée par un lien d'invitation académique */}
                    {inviteContext?.kind !== 'academic' && (
                      <fieldset>
                        <legend className="text-ink-700 text-sm font-medium">
                          Es-tu en alternance ?
                        </legend>
                        <div className="mt-1.5 grid grid-cols-2 gap-2">
                          <ChoiceChip selected={!isAlternant} onClick={() => setIsAlternant(false)}>
                            Non
                          </ChoiceChip>
                          <ChoiceChip selected={isAlternant} onClick={() => setIsAlternant(true)}>
                            Oui
                          </ChoiceChip>
                        </div>
                        {isAlternant && (
                          <p className="text-ink-500 mt-1.5 text-sm">
                            Tu pourras choisir ton tuteur académique et réserver des entretiens
                            gratuits depuis ton profil, juste après.
                          </p>
                        )}
                      </fieldset>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            <OnboardingFooter
              hint={!error && studentMissing ? `Il manque ${studentMissing}.` : undefined}
            >
              {errorAlert}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={loading2}
                disabled={!canSubmitStudent || consentEmailMissing || loading2}
              >
                {requiresConsent
                  ? 'Demander l’accord de mon parent'
                  : isHigherEd
                    ? 'Aller à mon tableau de bord'
                    : 'Continuer'}
              </Button>
            </OnboardingFooter>
          </form>
        )}

        {/* ── Étape 2a-bis : le diagnostic (plus d'objectifs depuis le 31/10/2026) ── */}
        {step === 'objectives' && level && (
          <div className={cn('space-y-4', ONBOARDING_FOOTER_SPACE)}>
            <OnboardingBack onClick={() => goBack('student')} />
            {diagnosticAvailable && (
              <OnboardingDiagnosticChoice value={diagnosticNow} onChange={setDiagnosticNow} />
            )}
            <OnboardingFooter>
              {errorAlert}
              <Button
                type="button"
                variant="primary"
                size="lg"
                fullWidth
                loading={loading2}
                onClick={handleObjectivesStep}
              >
                {onboardingFinishLabel(startDiagnostic, 0)}
              </Button>
            </OnboardingFooter>
          </div>
        )}

        {/* ── Étape 2b : espace parent ────────────────────────────────────── */}
        {step === 'parent' && (
          <div className={ONBOARDING_FOOTER_SPACE}>
            <OnboardingBack onClick={() => goBack('role')} />
            <Card className="border-ink-100/60 shadow-lg">
              <CardHeader className={CARD_HEADER}>
                <CardTitle>Tes enfants 👨‍👩‍👧</CardTitle>
                <CardDescription>
                  Crée le compte de ton enfant en quelques secondes, ou relie le compte qu’il a déjà
                  pour suivre sa progression.
                </CardDescription>
              </CardHeader>
              <CardContent className={cn(CARD_CONTENT, 'space-y-4')}>
                {errorAlert}
                <ParentChildrenOnboarding
                  parentId={user?.id ?? ''}
                  finishing={loading2}
                  onFinish={() => void handleFinish()}
                />
              </CardContent>
            </Card>
          </div>
        )}

        {/* ── Étape 2d : espace curieux/player ────────────────────────────── */}
        {step === 'player' && (
          <div className={ONBOARDING_FOOTER_SPACE}>
            <OnboardingBack onClick={() => goBack('role')} />
            <Card className="border-ink-100/60 shadow-lg">
              <CardHeader className={CARD_HEADER}>
                <CardTitle>Ton niveau 🎮</CardTitle>
                <CardDescription>
                  Tu refais des maths pour toi : à ton rythme, sans note ni obligation.
                </CardDescription>
              </CardHeader>
              <CardContent className={cn(CARD_CONTENT, 'space-y-4')}>
                {errorAlert}
                <PlayerOnboardingStep loading={loading2} onFinish={() => void handleFinish()} />
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
